<script lang="ts">
  /**
   * Quick-create-modal för POI som öppnas från översikts-kartans
   * "Lägg till POI"-flöde (S-04-17c-part2).
   *
   * Skiljer sig från PoisView-formen: minimalistisk, inga edit/delete-
   * features, koordinaterna är fixerade från kart-klicket. Submit
   * skickar createPoi och stänger sig själv.
   *
   * Hard-rules (matchar PoisView + avvikelse 7):
   *  - Server-side PII-detektor avvisar personnummer/telefon/e-post
   *  - Body-textarea har 200-cap counter (server-side hard cap)
   *  - Inga råa databas-ID:n
   *  - Användartexter på svenska
   */
  import {
    createPoi,
    PIN_KINDS,
    PIN_TYPES,
    pinKindLabel,
    pinTypeLabel,
    type CreatePoiInput,
    type PinKind,
    type PinType,
  } from '../pois/poisClient';

  type Props = {
    /** Lat/lng från kart-klicket. */
    latitude: number;
    longitude: number;
    /** Anropas vid lyckad create. Föräldern stänger modalen. */
    onCreated: () => void;
    /** Anropas vid avbryt eller backdrop-klick. */
    onCancel: () => void;
    /** Anropas vid 401/403 så App-shellen tvingar logout. */
    onUnauthorized?: () => void;
  };
  const { latitude, longitude, onCreated, onCancel, onUnauthorized }: Props = $props();

  let formPinType: PinType = $state('defibrillator');
  let formKind: PinKind = $state('info');
  let formTitle: string = $state('');
  let formBody: string = $state('');
  let formActiveTo: string = $state('');
  let formError: string | null = $state(null);
  let isSaving = $state(false);

  const BODY_MAX = 200;

  function localInputToIso(value: string): string | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
  }

  function validateForm(): string | null {
    if (formTitle.trim().length === 0) return 'Ange en titel.';
    if (formTitle.length > 120) return 'Titel max 120 tecken.';
    if (formBody.length > BODY_MAX) return `Beskrivning max ${BODY_MAX} tecken.`;
    if (formKind === 'notice' && !formActiveTo) {
      return 'Notiser kräver ett slut-datum.';
    }
    return null;
  }

  async function handleSubmit(): Promise<void> {
    formError = null;
    const validation = validateForm();
    if (validation) {
      formError = validation;
      return;
    }
    isSaving = true;
    try {
      const trimmedBody = formBody.trim();
      const activeToIso = localInputToIso(formActiveTo);
      const input: CreatePoiInput = {
        pinType: formPinType,
        kind: formKind,
        latitude,
        longitude,
        title: formTitle.trim(),
        body: trimmedBody.length > 0 ? trimmedBody : null,
        activeFrom: null,
        activeTo: activeToIso,
      };
      const result = await createPoi(input);
      if (result.kind === 'ok') {
        onCreated();
      } else if (result.kind === 'unauthorized') {
        onUnauthorized?.();
      } else if (result.kind === 'invalid') {
        formError = result.message;
      } else {
        formError =
          result.kind === 'forbidden'
            ? 'Saknar behörighet.'
            : result.kind === 'error'
              ? result.message
              : 'Något gick fel.';
      }
    } finally {
      isSaving = false;
    }
  }

  function handleBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && !isSaving) {
      onCancel();
    }
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && !isSaving) {
      onCancel();
    }
  }
</script>

<svelte:window onkeydown={handleKeyDown} />

<div
  class="modal-backdrop"
  role="dialog"
  aria-modal="true"
  aria-labelledby="poi-quick-title"
  onclick={handleBackdropClick}
>
  <div class="modal" role="document">
    <div class="modal-header">
      <p class="eyebrow">Ny POI</p>
      <h3 id="poi-quick-title">Placera markör vid {latitude.toFixed(4)}, {longitude.toFixed(4)}</h3>
    </div>

    <form
      class="form"
      onsubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
    >
      <div class="field-row">
        <label class="field">
          <span>Pin-typ</span>
          <select bind:value={formPinType} disabled={isSaving}>
            {#each PIN_TYPES as t (t)}
              <option value={t}>{pinTypeLabel(t)}</option>
            {/each}
          </select>
        </label>
        <label class="field">
          <span>Variant</span>
          <select bind:value={formKind} disabled={isSaving}>
            {#each PIN_KINDS as k (k)}
              <option value={k}>{pinKindLabel(k)}</option>
            {/each}
          </select>
        </label>
      </div>

      <label class="field">
        <span>Titel</span>
        <input type="text" maxlength="120" bind:value={formTitle} required disabled={isSaving} />
      </label>

      <label class="field">
        <span>Beskrivning ({formBody.length}/{BODY_MAX} tecken, valfri)</span>
        <textarea maxlength={BODY_MAX} rows="2" bind:value={formBody} disabled={isSaving}
        ></textarea>
      </label>

      {#if formKind === 'notice'}
        <label class="field">
          <span>Aktiv till (krävs för notis)</span>
          <input type="datetime-local" bind:value={formActiveTo} required disabled={isSaving} />
        </label>
      {/if}

      {#if formError}
        <div class="alert error" role="alert">{formError}</div>
      {/if}

      <p class="hint dim">
        Servern avvisar personnummer, telefon eller e-post i titel och beskrivning.
      </p>

      <div class="form-actions">
        <button type="submit" class="btn primary" disabled={isSaving}>
          {isSaving ? 'Sparar…' : 'Skapa POI'}
        </button>
        <button type="button" class="btn secondary" onclick={onCancel} disabled={isSaving}>
          Avbryt
        </button>
      </div>
    </form>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
  }
  .modal {
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 8px;
    padding: 1.5rem;
    max-width: 480px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    color: var(--text);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  }
  .modal-header {
    display: grid;
    gap: 0.3rem;
    margin-bottom: 1rem;
  }
  .eyebrow {
    font-size: 0.8rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--accent);
    font-weight: 600;
    margin: 0;
  }
  h3 {
    margin: 0;
    color: var(--text);
    font-size: 1.05rem;
  }
  .form {
    display: grid;
    gap: 0.85rem;
  }
  .field {
    display: grid;
    gap: 0.25rem;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  .field input,
  .field textarea,
  .field select {
    background: var(--bg);
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text);
    font-family: inherit;
    font-size: 0.95rem;
    padding: 0.45rem 0.6rem;
  }
  .field textarea {
    resize: vertical;
  }
  .field input:focus,
  .field textarea:focus,
  .field select:focus {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  .field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.85rem;
  }
  .form-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-top: 0.25rem;
  }
  .btn {
    background: transparent;
    border: 1px solid var(--rule);
    color: var(--text);
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-family: inherit;
    font-size: 0.9rem;
    cursor: pointer;
  }
  .btn.primary {
    background: var(--accent);
    border-color: var(--accent);
    color: var(--bg);
    font-weight: 600;
  }
  .btn.primary:hover:not(:disabled) {
    background: var(--glow);
    border-color: var(--glow);
  }
  .btn.secondary:hover:not(:disabled) {
    color: var(--glow);
    border-color: var(--text-dim);
  }
  .btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  .alert {
    padding: 0.55rem 0.8rem;
    border-radius: 4px;
    font-size: 0.85rem;
  }
  .alert.error {
    background: var(--danger-soft);
    border: 1px solid var(--danger);
    color: var(--text);
  }
  .hint {
    font-size: 0.75rem;
    margin: 0;
  }
  .dim {
    color: var(--text-dim);
  }
  @media (max-width: 540px) {
    .field-row {
      grid-template-columns: 1fr;
    }
  }
</style>
